// Ideas from books that are already part of daily life.
// Stored as a map keyed by id so two devices can each add one entry
// without replacing the other's whole list.

const ID_RE = /^[A-Za-z0-9_-]{1,80}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const asMap = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const clip = (value, max) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.slice(0, max);
};

export const localDateKey = (d = new Date()) => {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export const newIntegrationId = () => {
  const rand = Math.random().toString(36).slice(2, 8);
  return `i${Date.now().toString(36)}${rand}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
};

export const normalizeIntegrationEntry = (entry, fallbackId) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const fromEntry = typeof entry.id === 'string' ? entry.id : '';
  const fromKey = typeof fallbackId === 'string' ? fallbackId : String(fallbackId || '');
  const id = ID_RE.test(fromKey) ? fromKey : (ID_RE.test(fromEntry) ? fromEntry : '');
  if (!id) return null;
  const book = clip(entry.book, 200);
  const principle = clip(entry.principle, 300);
  const lifeArea = clip(entry.lifeArea, 200);
  const notes = clip(entry.notes, 2000);
  const deleted = entry.deleted === true;
  if (!deleted && !book && !principle) return null;
  const implementedOn = DATE_RE.test(entry.implementedOn) ? entry.implementedOn : '';
  const updatedAt = typeof entry.updatedAt === 'string' && entry.updatedAt ? entry.updatedAt : null;
  return { id, book, principle, lifeArea, notes, implementedOn, updatedAt, deleted };
};

export const normalizeIntegrations = (input) => {
  const out = {};
  if (Array.isArray(input)) {
    input.forEach((entry) => {
      const norm = normalizeIntegrationEntry(entry, entry?.id);
      if (norm) out[norm.id] = norm;
    });
    return out;
  }
  const map = asMap(input);
  if (!map) return out;
  Object.entries(map).forEach(([key, entry]) => {
    const norm = normalizeIntegrationEntry(entry, key);
    if (norm) out[norm.id] = norm;
  });
  return out;
};

const entryStamp = (entry) => (typeof entry?.updatedAt === 'string' ? entry.updatedAt : '');

// Union by id. The newer updatedAt wins, including a delete, so one device
// cannot resurrect an idea the other device already removed.
export const mergeIntegrations = (local, remote) => {
  const localMap = normalizeIntegrations(local);
  const remoteMap = normalizeIntegrations(remote);
  const out = { ...remoteMap };
  Object.entries(localMap).forEach(([id, loc]) => {
    const rem = out[id];
    if (!rem || entryStamp(loc) >= entryStamp(rem)) out[id] = loc;
  });
  return out;
};

const sameEntry = (a, b) => !!a && !!b
  && a.book === b.book
  && a.principle === b.principle
  && a.lifeArea === b.lifeArea
  && a.notes === b.notes
  && a.implementedOn === b.implementedOn
  && a.deleted === b.deleted
  && (a.updatedAt || null) === (b.updatedAt || null);

// Dotted paths only. A whole-map write would replace the other device's entries.
export const integrationCloudPatch = (cloud, merged) => {
  const cloudMap = normalizeIntegrations(cloud);
  const next = normalizeIntegrations(merged);
  const patch = {};
  Object.entries(next).forEach(([id, entry]) => {
    if (!sameEntry(cloudMap[id], entry)) patch[`integrations.${id}`] = entry;
  });
  return patch;
};

export const integrationEntryFromDraft = (draft, { id, now = new Date().toISOString() } = {}) => {
  const entry = normalizeIntegrationEntry({
    ...(draft && typeof draft === 'object' ? draft : {}),
    id: id || draft?.id || newIntegrationId(),
    deleted: false,
    updatedAt: now
  });
  if (!entry || !entry.book || !entry.principle || !entry.lifeArea) return null;
  return entry;
};

export const listIntegrations = (map) => Object.values(normalizeIntegrations(map))
  .filter((entry) => !entry.deleted)
  .sort((a, b) => {
    const byDate = (b.implementedOn || '').localeCompare(a.implementedOn || '');
    if (byDate) return byDate;
    return entryStamp(b).localeCompare(entryStamp(a));
  });

export const filterIntegrations = (entries, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return entries;
  return entries.filter((entry) => [entry.book, entry.principle, entry.lifeArea, entry.notes]
    .some((part) => String(part || '').toLowerCase().includes(q)));
};

export const groupIntegrationsByBook = (entries) => {
  const groups = new Map();
  entries.forEach((entry) => {
    const key = (entry.book || 'Untitled book').trim().toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  });
  return [...groups.values()]
    .map((items) => {
      const sorted = items.slice().sort((a, b) => {
        const byDate = (b.implementedOn || '').localeCompare(a.implementedOn || '');
        if (byDate) return byDate;
        return entryStamp(b).localeCompare(entryStamp(a));
      });
      const named = items.slice().sort((a, b) => entryStamp(b).localeCompare(entryStamp(a)));
      return {
        book: named.find((entry) => entry.book)?.book || 'Untitled book',
        entries: sorted
      };
    })
    .sort((a, b) => {
      const byDate = (b.entries[0]?.implementedOn || '').localeCompare(a.entries[0]?.implementedOn || '');
      if (byDate) return byDate;
      return a.book.localeCompare(b.book);
    });
};

export const formatIntegratedDate = (isoDate) => {
  if (!DATE_RE.test(isoDate || '')) return '';
  const [year, month, day] = isoDate.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

export const LIFE_AREA_SUGGESTIONS = [
  'Productivity and time management',
  'Health',
  'Money',
  'Training',
  'Relationships'
];
